/**
 * 思考方式选择器组件
 * 用于在组件/数据集配置时多选思考方式
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Select, message } from 'antd';
import type { ThinkingMode } from '../../services/aiConfigService';

const { Option } = Select;

interface ThinkingModeSelectorProps {
  value?: string[];  // 思考方式ID数组
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  [key: string]: any;  // 支持其他 props（如 Form.Item 传递的）
}

const ThinkingModeSelector: React.FC<ThinkingModeSelectorProps> = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const [thinkingModes, setThinkingModes] = useState<ThinkingMode[]>([]);
  const [loading, setLoading] = useState(false);

  // Mock: 加载思考方式列表
  const loadThinkingModes = useCallback(async (): Promise<ThinkingMode[]> => {
    // TODO: 替换为真实 API
    // return await aiConfigService.getThinkingModeList();
    return [
      { id: 'analyze', label: '数据分析', category: 'analysis' },
      { id: 'predict', label: '预测分析', category: 'prediction' },
      { id: 'optimize', label: '优化建议', category: 'optimization' },
    ];
  }, []);

  // 加载思考方式列表
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const thinkingModesData = await loadThinkingModes();
      setThinkingModes(thinkingModesData);
    } catch (error: any) {
      message.error(`加载思考方式失败: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  }, [loadThinkingModes]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (selectedIds: string[]) => {
    onChange?.(selectedIds);
  };

  // 确保 value 是数组格式
  const selectValue = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <Select
      mode="multiple"
      value={selectValue}
      onChange={handleChange}
      placeholder="选择思考方式（可多选）"
      style={{ width: '100%' }}
      disabled={disabled}
      loading={loading}
      allowClear
      showSearch
      notFoundContent={loading ? '加载中...' : thinkingModes.length === 0 ? '暂无思考方式' : '未找到'}
      filterOption={(input, option) => {
        // 简化搜索逻辑：直接使用 option 的 value 和 label 进行搜索
        const searchText = input.toLowerCase();
        const optionValue = String(option?.value || '').toLowerCase();
        const optionLabel = String(option?.label || '').toLowerCase();
        const optionChildren = String(option?.children || '').toLowerCase();
        
        return optionValue.includes(searchText) || 
               optionLabel.includes(searchText) || 
               optionChildren.includes(searchText);
      }}
    >
      {thinkingModes.length > 0 ? (
        thinkingModes.map((tm) => (
          <Option key={tm.id} value={tm.id}>
            {tm.label} ({tm.id})
          </Option>
        ))
      ) : (
        !loading && <Option disabled>暂无数据</Option>
      )}
    </Select>
  );
};

export default ThinkingModeSelector;
